import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Clock, User, Newspaper } from 'lucide-react';

const insights = [
  {
    id: 1,
    category: 'Entrepreneurship',
    title: 'Building Sustainable Businesses in Emerging Markets',
    excerpt: 'Key strategies for entrepreneurs looking to create lasting impact across African economies through innovation and sustainable practices.',
    image: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/1f9sk8dn_bcd7b376-55c9-49a7-ac2c-8257160a9f88.jpeg',
    readTime: '8 min',
    author: 'Kirit Sobti'
  },
  {
    id: 2,
    category: 'Africa Investment',
    title: 'Why Africa is the Next Frontier for Global Investors',
    excerpt: 'Exploring the untapped potential and emerging opportunities across the continent\'s fastest-growing sectors.',
    image: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/5bm5thtg_d7f53e28-0837-4280-93f5-f260909862bf.jpeg',
    readTime: '10 min',
    author: 'Kirit Sobti'
  },
  {
    id: 3,
    category: 'Leadership',
    title: 'Strategic Partnerships: The Key to Pan-African Success',
    excerpt: 'How building alliances with governments, institutions, and entrepreneurs creates exponential impact.',
    image: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/3f9gyyzz_2585ef15-8017-4d06-a25c-b2ba89332fd7.jpeg',
    readTime: '7 min',
    author: 'Kirit Sobti'
  },
];

export const InsightsSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section 
      id="insights" 
      className="section bg-[#080808] relative"
      data-testid="insights-section"
    >
      <div className="max-w-7xl mx-auto px-6" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12"
        >
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Content Hub</p>
            <h2 className="section-title">
              Insights & <span className="gradient-text">Wisdom</span>
            </h2>
          </div>
          <button className="flex items-center text-[#D4AF37] mt-6 lg:mt-0 hover:text-[#F4D06F] transition-colors">
            <span className="text-sm font-medium">View All Insights</span>
            <ArrowRight size={16} className="ml-2" />
          </button>
        </motion.div>

        {/* Insights Grid - 3 articles */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {insights.map((insight, index) => (
            <motion.article
              key={insight.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              className="insight-card card-hover group cursor-pointer"
              data-testid={`insight-${insight.id}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={insight.image}
                  alt={insight.title}
                  className="insight-card-image transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-60" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs bg-[#D4AF37] text-[#0A0A0A] font-medium">
                  {insight.category}
                </span>
              </div>

              <div className="insight-card-content">
                <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {insight.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed mb-4">
                  {insight.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-white/40">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {insight.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {insight.readTime}
                    </span>
                  </div>
                  <ArrowRight size={14} className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Times of Africa - Reframed */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="glass rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/8yod4ui6_d5b91698-f93e-4168-84d3-01ff07a1b57b.jpeg"
            alt="The Times of Africa"
            className="h-16 w-auto object-contain"
          />
          <div className="flex-1 text-center sm:text-left">
            <p className="text-white font-semibold mb-1">Founder of Times of Africa</p>
            <p className="text-sm text-white/50">
              The monthly magazine covering Africa-India business, read across 26 nations.
            </p>
          </div>
          <button className="btn-secondary text-sm whitespace-nowrap">
            Subscribe
          </button>
        </motion.div>
      </div>
    </section>
  );
};
